import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Edit, Plus, Trash2, Search } from "lucide-react";
import { getMenus } from "helpers/backend_helper";
import BreadCrumb from "Common/BreadCrumb";
import TableContainer from "Common/TableContainer";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Common } from "helpers/common";
import MenuForm from "./MenuForm";

const MenuList = () => {
    const [menus, setMenus] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [show, setShow] = useState<boolean>(false);
    const [menuData, setMenuData] = useState<any>(null);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [eventData, setEventData] = useState<any>();
    const uiHelper = new Common();

    useEffect(() => {
        fetchMenus();
    }, []);

    const fetchMenus = async () => {
        try {
            const response = await getMenus();
            setMenus(response.data || []);
        } catch (error) {
            console.error("Error fetching menus:", error);
        } finally {
            setLoading(false);
        }
    };

    // Delete Modal
    const deleteToggle = () => setDeleteModal(!deleteModal);

    // Delete Data
    const onClickDelete = (cell: any) => {
        setDeleteModal(true);
        if (cell.id) {
            setEventData(cell);
        }
    };

    const handleDelete = async () => {
        if (eventData) {
            // TODO: Implement delete menu API call
            setDeleteModal(false);
            uiHelper.showSuccessMessage('Menu has been deleted successfully.')
            setMenus(menus => menus.filter((menu: any) => menu.id !== eventData.id));
        }
    };

    // Update Data
    const handleEditDataClick = (ele: any) => {
        setMenuData({ ...ele });
        setIsEdit(true);
        setShow(true);
    };

    const handleAddMenu = () => {
        setMenuData({});
        setIsEdit(false);
        toggle();
    }

    const onSave = (result: any) => {
        if (isEdit) {
            setMenus(prevMenus =>
                prevMenus.map((menu: any) =>
                    menu.id === menuData.id ? { ...result } : { ...menu }
                )
            );
            setIsEdit(false);
        } else {
            setMenus((prevMenus) => [{ ...result }, ...prevMenus]);
        }
        toggle();
    }

    const toggle = useCallback(() => {
        setShow(!show);
        if (show) {
            setMenuData(null);
        }
    }, [show]);

    // columns
    const Status = ({ item }: any) => {
        switch (item) {
            case true:
                return (<span className="status px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent">Active</span>);
            case false:
                return (<span className="status px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-orange-100 border-transparent text-orange-500 dark:bg-orange-500/20 dark:border-transparent">InActive</span>);
        }
    };

    const columns = [
        {
            header: "Menu Name",
            accessorKey: "name",
            enableColumnFilter: true,
            enableSorting: true,
        },
        {
            header: "URL",
            accessorKey: "url",
            enableColumnFilter: true,
        },
        {
            header: "Icon",
            accessorKey: "icon",
            enableColumnFilter: false,
            cell: (cell: any) => (
                <div>
                    <div className="flex items-center justify-center size-10 font-medium rounded-full shrink-0 bg-slate-200 text-slate-800 dark:text-zink-50 dark:bg-zink-600">
                        {cell.row.original.icon ? 
                            <img src={cell.row.original.icon} alt="" className="h-10 rounded-full" /> 
                            : (cell.getValue().split(' ').map((word: any) => word.charAt(0)).join(''))}
                    </div>
                </div>
            ),
        },
        {
            header: "Parent Menu",
            accessorKey: "parentId",
            enableColumnFilter: true,
            cell: (cell: any) => {
                const parentMenu = menus.find(m => m.id === cell.getValue());
                return parentMenu ? parentMenu.name : 'None';
            }
        },
        {
            header: "Status",
            accessorKey: "isActive",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
                <Status item={cell.getValue()} />
            ),
        },
        {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
                <div className="flex justify-start gap-2">
                    <Link to="#!"
                        className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400"
                        onClick={() => {
                            const data = cell.row.original;
                            handleEditDataClick(data);
                        }}>
                        <Edit className="inline-block size-4 ltr:mr-1 rtl:ml-1" />
                    </Link>
                    <Link to="#!"
                        className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-red-500 bg-red-100 hover:text-white hover:bg-red-500 dark:bg-red-500/20 dark:hover:bg-red-500"
                        onClick={() => {
                            const menuData = cell.row.original;
                            onClickDelete(menuData);
                        }}>
                        <Trash2 className="inline-block size-4 ltr:mr-1 rtl:ml-1" />
                    </Link>
                </div>
            ),
        }
    ];

    return (
        <React.Fragment>
            <DeleteModal show={deleteModal} onHide={deleteToggle} onDelete={handleDelete} />
            <ToastContainer closeButton={false} limit={1} />
            
            <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12" style={{ marginTop: 15 }}>
                <div className="xl:col-span-12">
                    <div className="card" id="menuTable">
                        <div className="flex items-center border-y border-dashed" style={{ padding: 10 }}>
                            <h6 className="text-15 grow">Menu Management</h6>
                            <div className="shrink-0">
                                <button type="button" className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20" onClick={handleAddMenu}>
                                    <Plus className="inline-block size-4" /> <span className="align-middle">Add Menu</span>
                                </button>
                            </div>
                        </div>
                        {loading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-500"></div>
                            </div>
                        ) : (
                            <div>
                                {menus && menus?.length > 0 ? (
                                    <TableContainer
                                        isPagination={true}
                                        columns={columns}
                                        data={menus}
                                        customPageSize={10}
                                        divclassName="overflow-x-auto"
                                        tableclassName="w-full whitespace-nowrap"
                                        theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                        thclassName="px-3.5 py-1.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                        tdclassName="px-3.5 py-1.0 border-y border-slate-200 dark:border-zink-500"
                                        PaginationClassName="flex flex-col items-center gap-4 px-4 mt-3 md:flex-row"
                                        SearchPlaceholder='Search...'
                                        isGlobalFilter={false}
                                        isSelect={true}
                                    />
                                ) : (
                                    <div className="noresult">
                                        <div className="py-6 text-center">
                                            <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                                            <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Menu Form Modal */}
            <Modal show={show} onHide={toggle} id="defaultModal" modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600">
                {menuData ? <MenuForm onSave={onSave} onHide={toggle} data={menuData} /> : <></>}
            </Modal>
        </React.Fragment>
    );
};

export default MenuList; 